import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ChargeBasis, ChargeBasisStats } from '../../../../models/forwarding/charge-basis.model';
import { ChargeBasisService } from '../../../../services/forwarding/charge-basis.service';

@Component({
  selector: 'app-charge-basis-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './charge-basis-modal.html',
  styleUrl: './charge-basis-modal.css'
})
export class ChargeBasisModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  showForm = false;
  isEditMode = false;

  charges: ChargeBasis[] = [];
  filteredCharges: ChargeBasis[] = [];
  stats: ChargeBasisStats = {
    totalUnits: 0,
    airUnits: 0,
    seaLclUnits: 0,
    seaFclUnits: 0,
    universalUnits: 0
  };

  searchTerm = '';
  selectedMode = '';

  formData: Partial<ChargeBasis> = {};
  private subscription: Subscription = new Subscription();

  constructor(private chargeBasisService: ChargeBasisService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.chargeBasisService.charges$.subscribe(charges => {
        this.charges = charges;
        this.applyFilters();
        this.calculateStats();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  open(): void {
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    this.showForm = false;
  }

  openForm(charge?: ChargeBasis): void {
    if (charge) {
      this.isEditMode = true;
      this.formData = { ...charge };
    } else {
      this.isEditMode = false;
      this.formData = {
        internalName: '',
        displayName: '',
        appliesToAir: false,
        appliesToSeaLcl: false,
        appliesToSeaFcl: false,
        notes: '',
        isActive: true
      };
    }
    this.showForm = true;
  }

  closeForm(): void {
    this.showForm = false;
    this.formData = {};
  }

  editCharge(charge: ChargeBasis): void {
    this.openForm(charge);
  }

  saveCharge(): void {
    if (this.isEditMode && this.formData.id) {
      this.chargeBasisService.updateCharge(this.formData.id, this.formData);
    } else {
      this.chargeBasisService.addCharge(this.formData as Omit<ChargeBasis, 'id' | 'createdAt'>);
    }
    this.closeForm();
  }

  deleteCharge(id: number): void {
    if (confirm('Are you sure you want to delete this charge basis unit?')) {
      this.chargeBasisService.deleteCharge(id);
    }
  }

  applyFilters(): void {
    let filtered = [...this.charges];

    // Search filter
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.internalName.toLowerCase().includes(search) ||
        c.displayName.toLowerCase().includes(search) ||
        (c.notes && c.notes.toLowerCase().includes(search))
      );
    }

    // Mode filter
    if (this.selectedMode) {
      switch (this.selectedMode) {
        case 'AIR':
          filtered = filtered.filter(c => c.appliesToAir);
          break;
        case 'SEA_LCL':
          filtered = filtered.filter(c => c.appliesToSeaLcl);
          break;
        case 'SEA_FCL':
          filtered = filtered.filter(c => c.appliesToSeaFcl);
          break;
        case 'UNIVERSAL':
          filtered = filtered.filter(c => c.appliesToAir && c.appliesToSeaLcl && c.appliesToSeaFcl);
          break;
      }
    }

    this.filteredCharges = filtered;
  }

  private calculateStats(): void {
    this.stats.totalUnits = this.charges.filter(c => c.isActive).length;
    this.stats.airUnits = this.charges.filter(c => c.isActive && c.appliesToAir).length;
    this.stats.seaLclUnits = this.charges.filter(c => c.isActive && c.appliesToSeaLcl).length;
    this.stats.seaFclUnits = this.charges.filter(c => c.isActive && c.appliesToSeaFcl).length;
    this.stats.universalUnits = this.charges.filter(c =>
      c.isActive && c.appliesToAir && c.appliesToSeaLcl && c.appliesToSeaFcl
    ).length;
  }
}
